import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Edit2, KeyRound, Loader2, Power } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { CredentialPackForm } from './CredentialPackForm';

interface Courier { id: number; name: string; code: string; brand?: string; orderType: 'B2C' | 'B2B' }

interface Pack {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
  isLive: boolean;
  courierIds: number[];
  couriers: Courier[];
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

export function CredentialPacksPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Pack | null>(null);

  const { data: packs, isLoading } = useQuery({
    queryKey: ['credential-packs', { search, status }],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (status) params.isActive = status;
      return (await api.get<Pack[]>('/credential-packs', { params })).data;
    },
  });

  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/credential-packs/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['credential-packs'] }); toast.success('Status updated'); },
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Credentials</h1>
          <p className="text-slate-500 text-sm mt-1">
            One credential set can apply to many couriers. Useful when several services
            (e.g. all DTDC sub-accounts or all Delhivery variants) share the same login.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
          <Plus size={18} /> New Credential Pack
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 min-w-[260px]">
            <Search size={16} className="text-slate-400" />
            <input type="text" placeholder="Search by pack name or description..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
            <option value="">Any status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Pack Name</th>
                <th className="text-left px-4 py-3">Applies To (Couriers)</th>
                <th className="text-left px-4 py-3">Environment</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading...</td></tr>}
              {!isLoading && (packs?.length ?? 0) === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">
                  <KeyRound size={20} className="mx-auto mb-1 opacity-50" />
                  No credential packs yet. Click <strong>New Credential Pack</strong> to share login across multiple couriers.
                </td></tr>
              )}
              {packs?.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs">#{p.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    {p.description && <div className="text-xs text-slate-500">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {p.couriers.length === 0
                      ? <span className="text-xs text-amber-600">No couriers assigned</span>
                      : <div className="flex flex-wrap gap-1 max-w-md">
                          {p.couriers.slice(0, 6).map(c => (
                            <span key={c.id} className={`text-xs px-2 py-0.5 rounded ${c.orderType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                              {c.name} <span className="opacity-50 font-mono">#{c.id}</span>
                            </span>
                          ))}
                          {p.couriers.length > 6 && <span className="text-xs text-slate-500 self-center">+{p.couriers.length - 6} more</span>}
                        </div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.isLive ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                      {p.isLive ? 'LIVE' : 'TEST'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.updatedAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${p.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => toggle.mutate(p.id)}
                        className={`p-1.5 rounded ${p.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-success hover:bg-success/10'}`}
                        title={p.isActive ? 'Deactivate' : 'Activate'}>
                        {toggle.isPending ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)}
        title="New Credential Pack" subtitle="One credential set applied to many couriers" maxWidth="3xl">
        <CredentialPackForm onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title="Edit Credential Pack" subtitle={editing?.name} maxWidth="3xl">
        {editing && <CredentialPackForm packId={editing.id} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}
